import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import useMember from "@/hooks/useMember";
import { logActivity } from "@/lib/activityLog";
import BannerEditModal from "./BannerEditModal";

export interface MyBanner {
  id: number;
  title: string;
  subtitle: string;
  button_text: string;
  button_link: string;
  image_url: string;
  active: boolean;
  category: string | null;
  template_id: string | null;
  member_id: number;
  moderation_status: string;
  approval_token: string | null;
  reviewed_at: string | null;
  contact_info: { name?: string; whatsapp?: string; email?: string } | null;
}

const MAX_ACTIVE_BANNERS = 3;

function getCategoryLabel(banner: MyBanner): string {
  const match = banner.title.match(/^\[([^\]]*)\]/);
  if (match) return match[1];
  return banner.category || "Anúncio";
}

function getStatus(banner: MyBanner): { label: string; className: string } {
  if (banner.moderation_status === "pending") {
    return { label: "Em análise", className: "bg-amber-50 text-amber-700 border-amber-200" };
  }
  if (banner.moderation_status === "rejected") {
    return { label: "Recusado", className: "bg-red-50 text-red-700 border-red-200" };
  }
  if (banner.active) {
    return { label: "No ar", className: "bg-green-50 text-green-700 border-green-200" };
  }
  return { label: "Desativado", className: "bg-beige text-brown/60 border-nude" };
}

export default function MyBannersList() {
  const { member } = useMember();
  const [banners, setBanners] = useState<MyBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<MyBanner | null>(null);
  const [deleting, setDeleting] = useState<MyBanner | null>(null);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    if (!member?.id) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("banners")
        .select("*")
        .eq("member_id", member.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setBanners((data as MyBanner[]) || []);
    } catch {
      setError("Não foi possível carregar seus anúncios.");
    } finally {
      setLoading(false);
    }
  }, [member?.id]);

  useEffect(() => {
    if (member?.id) load();
  }, [member?.id, load]);

  async function handleDeactivate(banner: MyBanner) {
    if (!member) return;
    setActionBusy(banner.id);
    setActionError("");
    const { error: err } = await supabase
      .from("banners")
      .update({ active: false })
      .eq("id", banner.id)
      .eq("member_id", member.id);
    setActionBusy(null);
    if (err) {
      setActionError("Erro ao desativar o anúncio. Tente novamente.");
      return;
    }
    logActivity("banner_deactivate", { banner_id: banner.id, member_id: member.id });
    await load();
  }

  async function handleReactivate(banner: MyBanner) {
    if (!member) return;
    setActionBusy(banner.id);
    setActionError("");

    const { data: actives, error: countErr } = await supabase
      .from("banners")
      .select("id")
      .eq("member_id", member.id)
      .eq("active", true);

    if (countErr) {
      setActionBusy(null);
      setActionError("Erro ao verificar o limite. Tente novamente.");
      return;
    }

    if ((actives || []).length >= MAX_ACTIVE_BANNERS) {
      setActionBusy(null);
      setActionError(
        "Você já tem 3 anúncios no ar. Desative um deles na lista para liberar espaço e reativar este.",
      );
      return;
    }

    const { error: err } = await supabase
      .from("banners")
      .update({ active: true })
      .eq("id", banner.id)
      .eq("member_id", member.id);

    setActionBusy(null);
    if (err) {
      setActionError("Erro ao reativar o anúncio. Tente novamente.");
      return;
    }
    logActivity("banner_reactivate", { banner_id: banner.id, member_id: member.id });
    await load();
  }

  async function confirmDelete() {
    if (!deleting || !member) return;
    setActionBusy(deleting.id);
    setActionError("");
    const { error: err } = await supabase
      .from("banners")
      .delete()
      .eq("id", deleting.id)
      .eq("member_id", member.id);
    setActionBusy(null);
    setDeleting(null);
    if (err) {
      setActionError("Erro ao excluir o anúncio. Tente novamente.");
      return;
    }
    logActivity("banner_delete", { banner_id: deleting.id, member_id: member.id });
    await load();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Carregando seus anúncios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-2xl p-10 md:p-14">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-6">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <h3 className="font-display text-xl text-brown mb-3">Algo deu errado</h3>
          <p className="font-body text-brown/70 text-sm mb-6">{error}</p>
          <button
            onClick={load}
            className="font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (banners.length === 0) {
    return (
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-2xl p-10 md:p-14">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-6">
            <i className="ri-megaphone-line text-2xl text-primary" />
          </div>
          <h3 className="font-display text-xl md:text-2xl text-brown mb-3">
            Você ainda não tem anúncios
          </h3>
          <p className="font-body text-brown/70 text-sm leading-relaxed mb-8">
            Crie seu primeiro anúncio para divulgar seu negócio, oferecer ou procurar
            vagas e pedir indicações para a comunidade.
          </p>
          <Link
            to="/enviar-anuncio"
            className="inline-block font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            Criar anúncio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
          Área da integrante
        </p>
        <h2 className="font-display text-2xl md:text-3xl text-brown mb-3">Meus anúncios</h2>
        <p className="font-body text-brown/70 text-sm leading-relaxed max-w-lg mx-auto mb-6">
          Acompanhe o status dos seus anúncios, edite o conteúdo ou desative quando quiser.
        </p>
        <Link
          to="/enviar-anuncio"
          className="inline-flex items-center gap-2 font-body text-sm px-6 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Criar novo anúncio
        </Link>
      </div>

      {actionError && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm flex items-start gap-2 mb-6">
          <i className="ri-error-warning-line mt-0.5 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {/* List */}
      <div className="space-y-4">
        {banners.map((banner) => {
          const status = getStatus(banner);
          const isLive = banner.moderation_status === "approved" && banner.active;
          const isOff = banner.moderation_status === "approved" && !banner.active;
          const busy = actionBusy === banner.id;

          return (
            <div
              key={banner.id}
              className="bg-white rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row gap-4 border border-nude/60"
            >
              {/* Thumbnail */}
              <div className="w-full sm:w-44 shrink-0">
                <div className="aspect-[1440/400] w-full rounded-lg overflow-hidden bg-beige">
                  <img
                    src={banner.image_url}
                    alt={banner.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info + actions */}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="font-body text-xs text-brown/50 uppercase tracking-wide mb-1">
                      {getCategoryLabel(banner)}
                    </p>
                    <h3 className="font-display text-base md:text-lg text-brown leading-snug line-clamp-2">
                      {banner.title.replace(/^\[[^\]]*\]\s*/, "")}
                    </h3>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-body font-medium ${status.className}`}
                  >
                    <i
                      className={`text-xs ${
                        status.label === "No ar"
                          ? "ri-checkbox-circle-line"
                          : status.label === "Em análise"
                            ? "ri-time-line"
                            : status.label === "Recusado"
                              ? "ri-close-circle-line"
                              : "ri-pause-circle-line"
                      }`}
                    />
                    {status.label}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-auto pt-3">
                  <button
                    onClick={() => setEditing(banner)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-body tracking-wide hover:bg-primary hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line" />
                    Editar
                  </button>

                  {isLive && (
                    <button
                      onClick={() => handleDeactivate(banner)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-nude text-brown text-sm font-body tracking-wide hover:bg-beige transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-pause-line" />
                      )}
                      Desativar
                    </button>
                  )}

                  {isOff && (
                    <button
                      onClick={() => handleReactivate(banner)}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary text-primary text-sm font-body tracking-wide hover:bg-primary hover:text-white transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-play-line" />
                      )}
                      Reativar
                    </button>
                  )}

                  <button
                    onClick={() => setDeleting(banner)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-red-600 text-sm font-body tracking-wide hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                    Excluir
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editing && member && (
        <BannerEditModal
          banner={editing}
          member={{ id: member.id, full_name: member.full_name, email: member.email }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {/* Delete confirmation modal */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDeleting(null)}
          />
          <div className="relative bg-white rounded-2xl p-8 w-full max-w-md text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-5">
              <i className="ri-delete-bin-line text-xl text-red-500" />
            </div>
            <h3 className="font-display text-lg text-brown mb-2">Excluir anúncio?</h3>
            <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
              Tem certeza que deseja excluir{" "}
              <strong>{deleting.title.replace(/^\[[^\]]*\]\s*/, "")}</strong>? Essa ação não
              pode ser desfeita e libera espaço no seu limite de 3 anúncios.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={confirmDelete}
                disabled={actionBusy === deleting.id}
                className="font-body text-sm px-6 py-3 rounded-full bg-red-600 text-white tracking-wide uppercase hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionBusy === deleting.id ? "Excluindo..." : "Excluir de vez"}
              </button>
              <button
                onClick={() => setDeleting(null)}
                className="font-body text-sm px-6 py-3 rounded-full border border-nude text-brown tracking-wide uppercase hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}